import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

export type WhatsappResultado =
  | { ok: true; e164: string; aviso?: string }
  | { ok: false; erro: string };

/**
 * Normaliza um número de WhatsApp para E.164 (RN-CLI-01).
 * - Sem "+" e com 10–11 dígitos ⇒ assume Brasil (+55).
 * - Não insere o 9º dígito automaticamente.
 */
export function normalizarWhatsapp(entrada: string): WhatsappResultado {
  const bruto = (entrada ?? '').trim();
  if (!bruto) return { ok: false, erro: 'Informe o WhatsApp.' };

  const temMais = bruto.startsWith('+');
  let digitos = bruto.replace(/\D/g, '');
  if (!temMais) {
    if (digitos.startsWith('0')) digitos = digitos.replace(/^0+/, '');
    if (digitos.length === 10 || digitos.length === 11) digitos = `55${digitos}`;
  }
  const numero = parsePhoneNumberFromString(`+${digitos}`);
  if (!numero || !numero.isValid()) {
    return { ok: false, erro: 'WhatsApp inválido. Use DDD + número, ex.: (11) 99876-0001.' };
  }
  const tipo = numero.getType();
  const e164 = numero.number;
  if (numero.countryCallingCode === '55' && numero.nationalNumber.length === 10 && tipo !== 'FIXED_LINE') {
    return { ok: true, e164, aviso: 'Celular com 8 dígitos: confira se falta o 9.' };
  }
  if (tipo === 'FIXED_LINE') {
    return { ok: true, e164, aviso: 'O número parece ser fixo. Confirme se tem WhatsApp.' };
  }
  return { ok: true, e164 };
}

/**
 * Celular no formato antigo (antes do 9º dígito), como a Receita e as APIs de CNPJ ainda trazem:
 * DDD + 8 dígitos começando com 6–9 → acrescenta o 9 (ex.: 45 9990-7568 → 45 99990-7568).
 * Fixo (2–5) e número já com 9 dígitos ficam como estão. Só para dados de terceiros, não para o que o usuário digita.
 */
export function corrigirCelularAntigo(t: string): string {
  const d = t.replace(/\D/g, '').replace(/^0+/, '');
  return /^[1-9][0-9][6-9][0-9]{7}$/.test(d) ? `${d.slice(0, 2)}9${d.slice(2)}` : t;
}

/** Exibe no formato nacional: (11) 99876-0001 */
export function formatarWhatsapp(e164: string | null | undefined): string {
  if (!e164) return '—';
  const n = parsePhoneNumberFromString(e164);
  if (!n) return e164;
  return n.countryCallingCode === '55' ? n.formatNational() : n.formatInternational();
}

export const WA_ME_MAX_URL = 2000;

/** Monta o link wa.me (RN-CAM-07). */
export function linkWaMe(e164: string, mensagem: string): string {
  const digitos = e164.replace(/\D/g, '');
  return `https://wa.me/${digitos}?text=${encodeURIComponent(mensagem)}`;
}

/**
 * Link do app WhatsApp Desktop (Windows/macOS). Não abre aba no navegador:
 * o app pula para a conversa com o texto pronto (fica como rascunho até o vendedor enviar).
 */
export function linkWhatsappApp(e164: string, mensagem: string): string {
  const digitos = e164.replace(/\D/g, '');
  return `whatsapp://send?phone=${digitos}&text=${encodeURIComponent(mensagem)}`;
}
